import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiService } from '../../../services/apiService';
import { baseURL } from '../../../services/apiConfig';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import LoadingSpinner from './LoadingSpinner';

const AuthenticatedImage = ({ src, alt, className = '', ...props }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { semantic } = useColors();
  const { replace } = useTextReplacement();

  useEffect(() => {
    let objectUrl;

    const fetchImage = async () => {
      try {
        setLoading(true);

        // NEW: Handle enhanced format {path, source, metadata, status}
        // Enhanced format has 'path' property - construct display URL from source
        if (src && typeof src === 'object' && src.path) {
          // Enhanced format: construct display URL from source information
          if (src.source && src.source.type === 'mediaLibrary' && src.source.id) {
            // MediaLibrary: use /mediaLibrary/{id} endpoint
            const resolvedUrl = `${baseURL}mediaLibrary/${src.source.id}`;
            setImageSrc(resolvedUrl);
            setLoading(false);
            return;
          } else if (src.source && src.source.type === 'userAsset' && src.source.id) {
            // UserAsset: needs authenticated fetch
            const response = await apiService.getUserAssetById(src.source.id, { asBlob: true });
            if (!(response instanceof Blob)) {
              throw new Error('Server response is not a blob');
            }
            objectUrl = URL.createObjectURL(response);
            setImageSrc(objectUrl);
            setError(null);
            return;
          } else {
            // No source info - can't display (legacy path)
            // This is a template-relative path that can't be displayed in React app
            setError(new Error('Enhanced format missing source information'));
            setLoading(false);
            return;
          }
        }

        // Handle structured reference objects (legacy format)
        if (src && typeof src === 'object' && src.type) {
          let resolvedUrl;
          if (src.type === 'userAsset') {
            // User asset - needs authenticated fetch
            const response = await apiService.getUserAssetById(src.id, { asBlob: true });
            if (!(response instanceof Blob)) {
              throw new Error('Server response is not a blob');
            }
            objectUrl = URL.createObjectURL(response);
            setImageSrc(objectUrl);
            setError(null);
            return;
          } else if (src.type === 'mediaLibrary') {
            // Media library - public endpoint, construct URL
            // Note: In development, baseURL is 'http://localhost:4242/'
            // In production, baseURL is same origin
            resolvedUrl = `${baseURL}mediaLibrary/${src.id}`;
            setImageSrc(resolvedUrl);
            setLoading(false);
            return;
          }
        }

        // Legacy: Handle URL strings
        if (typeof src === 'string') {
          // Check if this is a protected URL that needs authentication
          const isProtected = src.includes('/api/userAssets/file/');

          if (!isProtected) {
            // For public URLs, just use the src directly and bypass fetching
            setImageSrc(src);
            setLoading(false);
            return;
          }

          // Extract the asset ID from the src URL and request it as a blob
          const assetId = src.split('/').pop();
          const response = await apiService.getUserAssetById(assetId, { asBlob: true });

          // Ensure we have a valid blob
          if (!(response instanceof Blob)) {
            throw new Error('Server response is not a blob');
          }

          // Verify the blob contains image data
          if (response.type && !response.type.startsWith('image/')) {
            console.warn('AuthenticatedImage: unexpected blob type:', response.type, 'for asset', assetId);
          }

          objectUrl = URL.createObjectURL(response);
          setImageSrc(objectUrl);
          setError(null);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (src && src !== 'none') {
      fetchImage();
    } else {
      setLoading(false);
    }

    // Cleanup function to revoke the object URL
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center h-full w-full ${className}`}
        role="status"
        aria-label={replace('Loading image')}
      >
        <LoadingSpinner size="sm" message="" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center h-full w-full ${semantic.highlightDangerText} ${className}`}
        role="alert"
        aria-label={replace('Error loading image')}
      >
        {replace('Error loading image')}
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={replace(alt || 'Image')}
      className={`w-full h-full object-contain ${className}`}
      {...props}
    />
  );
};

AuthenticatedImage.propTypes = {
  src: PropTypes.oneOfType([
    PropTypes.string,
    // Legacy structured reference
    PropTypes.shape({
      type: PropTypes.oneOf(['mediaLibrary', 'userAsset']).isRequired,
      id: PropTypes.string.isRequired,
      source: PropTypes.oneOf(['library', 'uploads', 'generated'])
    }),
    // Enhanced format
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      source: PropTypes.shape({
        type: PropTypes.oneOf(['mediaLibrary', 'userAsset']),
        id: PropTypes.string,
        sourcePath: PropTypes.string,
        sourceType: PropTypes.number,
        source: PropTypes.string
      }),
      metadata: PropTypes.object,
      status: PropTypes.object
    })
  ]).isRequired,
  alt: PropTypes.string,
  className: PropTypes.string
};

export default AuthenticatedImage;

